import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Mail, ExternalLink, Heart, Users, BookOpen } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="bg-gray-900 border-t border-gray-800">
      {/* Organization Introduction Section - Compact */}
      <div className="bg-gray-800/50 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
            {/* Left side - Organization info */}
            <div>
              <div className="flex items-center space-x-3 mb-3">
                <div className="p-2 bg-blue-600 rounded-lg">
                  <Heart className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Fight for Vaquita</h2>
                  <p className="text-blue-400 text-xs">Student-Run Conservation Initiative</p>
                </div>
              </div>
              
              <p className="text-gray-300 mb-3 text-sm leading-relaxed">
                We are a nonprofit, student-run organization dedicated to raising awareness about the critically endangered vaquita porpoise and supporting conservation efforts in the Gulf of California.
              </p>
              
              <div className="flex items-center space-x-4 text-xs text-gray-400">
                <div className="flex items-center space-x-1">
                  <Users className="h-3 w-3 text-blue-400" />
                  <span>Student-Led</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Heart className="h-3 w-3 text-red-400" />
                  <span>Conservation Focus</span>
                </div>
                <div className="flex items-center space-x-1">
                  <BookOpen className="h-3 w-3 text-green-400" />
                  <span>Educational Mission</span>
                </div>
              </div>
            </div>
            
            {/* Right side - Contact CTA */}
            <div className="text-center lg:text-right">
              <h3 className="text-base font-semibold text-white mb-2">Interested in Collaborating?</h3>
              <p className="text-gray-300 mb-3 text-sm">
                We welcome partnerships with students, educators, and conservation organizations.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-2 justify-center lg:justify-end">
                <a 
                  href="mailto:contact@fightforvaquita.org"
                  className="inline-flex items-center justify-center px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded transition-colors duration-200"
                >
                  <Mail className="h-3 w-3 mr-1" />
                  Contact Us
                </a>
                <a 
                  href="https://instagram.com/fightforvaquita"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-3 py-2 border border-gray-600 text-gray-300 hover:bg-gray-800 text-sm font-medium rounded transition-colors duration-200"
                >
                  <Instagram className="h-3 w-3 mr-1" />
                  Follow Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Content - Very Compact */}
      <div className="py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* About Column */}
            <div>
              <h4 className="text-sm font-semibold text-white mb-2">About the Vaquita</h4>
              <ul className="space-y-1 text-gray-300 text-xs">
                <li>
                  <Link to="/about" className="hover:text-blue-400 transition-colors duration-200">
                    Species Profile
                  </Link>
                </li>
                <li>
                  <Link to="/threats" className="hover:text-blue-400 transition-colors duration-200">
                    Threats & Challenges
                  </Link>
                </li>
                <li>
                  <a 
                    href="https://vaquitacpr.org" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:text-blue-400 transition-colors duration-200 flex items-center"
                  >
                    Vaquita CPR Project
                    <ExternalLink className="h-2 w-2 ml-1" />
                  </a>
                </li>
                <li>
                  <a 
                    href="https://porpoise.org" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:text-blue-400 transition-colors duration-200 flex items-center"
                  >
                    Porpoise Conservation Society
                    <ExternalLink className="h-2 w-2 ml-1" />
                  </a>
                </li>
              </ul>
            </div>

            {/* Take Action Column */}
            <div>
              <h4 className="text-sm font-semibold text-white mb-2">Take Action</h4>
              <ul className="space-y-1 text-gray-300 text-xs">
                <li>
                  <Link to="/action" className="hover:text-blue-400 transition-colors duration-200">
                    Ways to Help
                  </Link>
                </li>
                <li>
                  <a 
                    href="https://www.change.org/search?q=vaquita" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:text-blue-400 transition-colors duration-200 flex items-center"
                  >
                    Sign Petitions
                    <ExternalLink className="h-2 w-2 ml-1" />
                  </a>
                </li>
                <li>
                  <a 
                    href="https://www.worldwildlife.org/species/vaquita" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:text-blue-400 transition-colors duration-200 flex items-center"
                  >
                    Support WWF
                    <ExternalLink className="h-2 w-2 ml-1" />
                  </a>
                </li>
                <li>
                  <a 
                    href="https://www.seashepherdglobal.org/campaigns/operation-milagro/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:text-blue-400 transition-colors duration-200 flex items-center"
                  >
                    Sea Shepherd
                    <ExternalLink className="h-2 w-2 ml-1" />
                  </a>
                </li>
              </ul>
            </div>

            {/* Resources Column */}
            <div>
              <h4 className="text-sm font-semibold text-white mb-2">Educational Resources</h4>
              <ul className="space-y-1 text-gray-300 text-xs">
                <li>
                  <Link to="/resources" className="hover:text-blue-400 transition-colors duration-200">
                    Research Papers
                  </Link>
                </li>
                <li>
                  <a 
                    href="https://www.iucnredlist.org/species/17028/50370296" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:text-blue-400 transition-colors duration-200 flex items-center"
                  >
                    IUCN Red List
                    <ExternalLink className="h-2 w-2 ml-1" />
                  </a>
                </li>
                <li>
                  <a 
                    href="https://www.noaa.gov/news/vaquita-porpoise-nears-extinction" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:text-blue-400 transition-colors duration-200 flex items-center"
                  >
                    NOAA Reports
                    <ExternalLink className="h-2 w-2 ml-1" />
                  </a>
                </li>
                <li>
                  <a 
                    href="https://www.nature.com/subjects/conservation-biology" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:text-blue-400 transition-colors duration-200 flex items-center"
                  >
                    Conservation Biology
                    <ExternalLink className="h-2 w-2 ml-1" />
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact & Social Column */}
            <div>
              <h4 className="text-sm font-semibold text-white mb-2">Connect</h4>
              <ul className="space-y-1 text-gray-300 text-xs">
                <li>
                  <a 
                    href="mailto:contact@fightforvaquita.org"
                    className="hover:text-blue-400 transition-colors duration-200 flex items-center"
                  >
                    <Mail className="h-3 w-3 mr-1" />
                    Contact Us
                  </a>
                </li>
                <li>
                  <a 
                    href="https://instagram.com/fightforvaquita"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-pink-400 transition-colors duration-200 flex items-center"
                  >
                    <Instagram className="h-3 w-3 mr-1" />
                    @fightforvaquita
                  </a>
                </li>
              </ul>
        
            </div>
          </div>
        </div>
      </div>

      {/* Copyright Section */}
      <div className="border-t border-gray-800 py-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-2 sm:space-y-0">
            <div className="text-xs text-gray-400">
              © 2025 Fight for Vaquita. Student-run conservation initiative. 
              <span className="block sm:inline mt-1 sm:mt-0 sm:ml-2">
                Made with ❤️ for marine conservation.
              </span>
            </div>
            <div className="flex items-center space-x-3 text-xs text-gray-400">
              <Link to="/privacy" className="hover:text-blue-400 transition-colors duration-200">
                Privacy Policy
              </Link>
              <span>•</span>
              <Link to="/terms" className="hover:text-blue-400 transition-colors duration-200">
                Terms of Use
              </Link>
              <span>•</span>
              <a 
                href="mailto:contact@fightforvaquita.org"
                className="hover:text-blue-400 transition-colors duration-200"
              >
                Contact
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;